import { Head, router } from "@inertiajs/react"
import { ChevronDown, ChevronRight, Info, ReceiptText } from "lucide-react"
import React, { useState } from "react"

import { Empty } from "@/components/ui/empty"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import PartnerLayout from "@/layouts/partner-layout"
import { partnerBillingPeriodsPath } from "@/routes"

interface PeriodOption {
  uid: string
  label: string
  period_start: string
}

interface AgentLine {
  uid: string
  agent_name: string
  wholesale_price_cents: number
  retail_price_cents: number | null
  prorated: boolean
  active_days: number | null
  period_days: number | null
}

interface AccountRow {
  account_uid: string
  account_name: string
  agent_count: number
  wholesale_total_cents: number
  retail_total_cents: number
  agents: AgentLine[]
}

interface SelectedPeriod {
  uid: string
  label: string
  total_wholesale_cents: number
  total_retail_cents: number
  total_profit_cents: number
  client_count: number
  markup_percentage: number
  due_date: string
  state: string
  accounts: AccountRow[]
}

function fmt(cents: number) {
  return "$" + Math.round(cents / 100).toLocaleString()
}

export default function BillingIndex({
  periods,
  selected_period,
}: {
  periods: PeriodOption[]
  selected_period: SelectedPeriod | null
}) {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  const selectedUid = selected_period?.uid ?? periods[0]?.uid ?? ""

  function handlePeriodChange(uid: string) {
    router.visit(partnerBillingPeriodsPath({ period: uid }), {
      preserveState: true,
    })
  }

  function toggleSection(accountUid: string) {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(accountUid)) next.delete(accountUid)
      else next.add(accountUid)
      return next
    })
  }

  if (!selected_period) {
    return (
      <PartnerLayout>
        <Head title="Billing" />
        <div className="flex flex-col gap-6">
          <h1 className="text-2xl/8 font-semibold">Billing</h1>
          <Empty
            icon={<ReceiptText className="h-5 w-5" />}
            title="No billing periods yet"
            description="Billing statements will appear here once your accounts start being billed."
          />
        </div>
      </PartnerLayout>
    )
  }

  const p = selected_period

  return (
    <PartnerLayout>
      <Head title="Billing" />
      <div className="flex flex-col gap-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl/8 font-semibold">Billing</h1>
          <Select value={selectedUid} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periods.map((m) => (
                <SelectItem key={m.uid} value={m.uid}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary cards */}
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border px-6 py-5">
            <div className="text-muted-foreground flex items-center justify-between text-sm font-medium">
              Retail
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="text-muted-foreground size-3.5 cursor-default" />
                </TooltipTrigger>
                <TooltipContent className="max-w-56">
                  Total amount billed to accounts at retail pricing for this
                  period.
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="mt-4">
              <span className="text-3xl font-normal tracking-tight">
                {fmt(p.total_retail_cents)}
              </span>
            </div>
          </div>
          <div className="rounded-lg border px-6 py-5">
            <div className="text-muted-foreground flex items-center justify-between text-sm font-medium">
              Wholesale
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="text-muted-foreground size-3.5 cursor-default" />
                </TooltipTrigger>
                <TooltipContent className="max-w-56">
                  Amount owed to Imperative for agent usage for this period.
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="mt-4">
              <span className="text-3xl font-normal tracking-tight">
                {fmt(p.total_wholesale_cents)}
              </span>
            </div>
          </div>
          <div className="rounded-lg border px-6 py-5">
            <div className="text-muted-foreground flex items-center justify-between text-sm font-medium">
              Profit
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="text-muted-foreground size-3.5 cursor-default" />
                </TooltipTrigger>
                <TooltipContent className="max-w-56">
                  Retail revenue minus wholesale costs for this period.
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="mt-4">
              <span className="text-3xl font-normal tracking-tight text-green-600">
                {fmt(p.total_profit_cents)}
              </span>
            </div>
          </div>
        </div>

        {/* Statement table */}
        <div>
          <div className="mb-3 text-sm font-semibold">
            Statement — {p.label}
          </div>
          {p.accounts.length === 0 ? (
            <Empty
              icon={<ReceiptText className="h-5 w-5" />}
              title="No billed accounts"
              description="No accounts were billed in this period."
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="text-foreground min-w-full text-left text-sm/6">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="w-7 border-b py-2 pl-1" />
                      <th className="border-b px-4 py-2 font-medium">Client</th>
                      <th className="border-b px-4 py-2 text-right font-medium">
                        Agents
                      </th>
                      <th className="border-b px-4 py-2 text-right font-medium">
                        Wholesale
                      </th>
                      <th className="border-b px-4 py-2 pr-1 text-right font-medium">
                        Retail
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.accounts.map((account) => {
                      const isOpen = openSections.has(account.account_uid)
                      return (
                        <React.Fragment key={account.account_uid}>
                          <tr
                            onClick={() => toggleSection(account.account_uid)}
                            className={`hover:bg-muted/50 cursor-pointer ${isOpen ? "bg-muted/50" : ""}`}
                          >
                            <td
                              className={`relative py-4 pr-0 pl-1 ${isOpen ? "" : "border-b"}`}
                            >
                              {isOpen ? (
                                <ChevronDown className="text-muted-foreground size-4 shrink-0" />
                              ) : (
                                <ChevronRight className="text-muted-foreground size-4 shrink-0" />
                              )}
                            </td>
                            <td className="border-b px-4 py-4 font-medium">
                              {account.account_name}
                            </td>
                            <td className="text-muted-foreground border-b px-4 py-4 text-right tabular-nums">
                              {account.agent_count}
                            </td>
                            <td className="text-muted-foreground border-b px-4 py-4 text-right tabular-nums">
                              {fmt(account.wholesale_total_cents)}
                            </td>
                            <td className="border-b px-4 py-4 pr-1 text-right font-medium tabular-nums">
                              {fmt(account.retail_total_cents)}
                            </td>
                          </tr>

                          {isOpen &&
                            account.agents.map((agent, idx) => {
                              const isLast = idx === account.agents.length - 1
                              return (
                                <tr
                                  key={agent.uid}
                                  className="bg-muted/50 hover:bg-muted"
                                >
                                  <td className="relative py-3 pr-0 pl-1">
                                    <span
                                      className={`bg-border absolute left-3 w-px ${isLast ? "top-0 bottom-1/2" : "inset-y-0"}`}
                                    />
                                  </td>
                                  <td className="border-b px-4 py-3">
                                    <span className="text-muted-foreground">
                                      {agent.agent_name}
                                    </span>
                                    <span className="text-muted-foreground/60 mt-0.5 block text-sm">
                                      {agent.prorated
                                        ? `Prorated · ${agent.active_days} of ${agent.period_days} days`
                                        : "Full month"}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3" />
                                  <td className="text-muted-foreground px-4 py-3 text-right tabular-nums">
                                    {fmt(agent.wholesale_price_cents)}
                                  </td>
                                  <td className="px-4 py-3 pr-1 text-right font-medium tabular-nums">
                                    {fmt(agent.retail_price_cents ?? 0)}
                                  </td>
                                </tr>
                              )
                            })}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer summary */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm/6">
                  <tbody>
                    <tr>
                      <td className="text-muted-foreground px-4 py-4 first:pl-1">
                        Retail
                      </td>
                      <td className="px-4 py-4 text-right font-medium tabular-nums last:pr-1">
                        {fmt(p.total_retail_cents)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted-foreground px-4 py-4 first:pl-1">
                        Wholesale
                      </td>
                      <td className="px-4 py-4 text-right font-medium tabular-nums last:pr-1">
                        {fmt(p.total_wholesale_cents)}
                      </td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-4 py-4 font-medium first:pl-1">
                        Profit
                      </td>
                      <td className="px-4 py-4 text-right font-semibold text-green-600 tabular-nums last:pr-1">
                        {fmt(p.total_profit_cents)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </PartnerLayout>
  )
}
